/**
 * Base Typography Scale
 * 
 * Based on Figma Design System 2026 typography specifications.
 * All values extracted from Figma design tokens.
 * 
 * @see https://www.figma.com/design/LYeg7o8lYMFZD4oPJnGTwn/Design-System-2026?node-id=7-3
 */
const baseTypography = {
  /** Font families */
  fontFamily: {
    /** Manrope - Primary sans-serif font for UI components */
    manrope: 'Manrope, sans-serif',
  },
  
  /** Font size scale (from Figma) */
  fontSize: {
    /** 10px - Caption small */
    xs: '10px',
    /** 12px - Caption medium */
    sm: '12px',
    /** 14px - Body small, button small */
    md: '14px',
    /** 16px - Body medium, button medium */
    lg: '16px',
    /** 18px - H6 */
    lgPlus: '18px',
    /** 20px - H5, button large */
    xl: '20px',
    /** 24px - H4 */
    xxl: '24px',
    /** 32px - H3 */
    xxxl: '32px',
    /** 40px - H2 */
    xxxxl: '40px',
    /** 48px - H1 */
    xxxxxl: '48px',
    /** 64px - Display medium */
    displayMd: '64px',
    /** 80px - Display large */
    displayLg: '80px',
  },
  
  /**
   * Responsive font size scales
   * Use these for components that need to scale across breakpoints
   * Format: { xs, sm, md, lg, xl } matching MUI breakpoints
   */
  responsive: {
    /** Display large: 28px → 40px - For prominent numeric/price displays */
    displayLarge: {
      xs: '28px',
      sm: '32px',
      md: '36px',
      lg: '38px',
      xl: '40px',
    },
    /** Heading large: 18px → 26px - For primary headings */
    headingLarge: {
      xs: '18px',
      sm: '20px',
      md: '22px',
      lg: '24px',
      xl: '26px',
    },
    /** Heading medium: 14px → 22px - For secondary headings */
    headingMedium: {
      xs: '14px',
      sm: '16px',
      md: '18px',
      lg: '20px',
      xl: '22px',
    },
  },
  
  /** Font weight scale (from Figma) */
  fontWeight: {
    /** 400 - Regular/Normal text (display styles) */
    regular: 400,
    /** 500 - Medium emphasis (h1, h2, body, button, captions) */
    medium: 500,
    /** 600 - Semibold (h3-h6, body-bold, caption-bold) */
    semibold: 600,
    /** 700 - Bold (caption-sm-bold) */
    bold: 700,
  },
  
  /** Line height scale (from Figma - using multipliers) */
  lineHeight: {
    /** 1.1 - Display styles, h2 */
    tight: 1.1,
    /** 1.2 - h1, h3, h4 */
    base: 1.2,
    /** 1.3 - h5, h6, button-lg, button-md */
    comfortable: 1.3,
    /** 1.4 - body-md, button-sm */
    relaxed: 1.4,
    /** 1.5 - body-sm, captions */
    loose: 1.5,
    /** 1.8 - body-double variants */
    extraLoose: 1.8,
  },
  
  /** Letter spacing scale */
  letterSpacing: {
    /** 0px - All Figma styles use 0 */
    normal: '0px',
  },
} as const;

/**
 * Text Style Preset Interface
 */
export interface TextStyle {
  fontSize: string;
  fontWeight: number;
  lineHeight: number | string;
  letterSpacing: string;
  fontFamily?: string;
}

/**
 * Text Style Presets (Figma Design System 2026)
 * 
 * Exact specifications from Figma typography system.
 * All values match Figma design tokens precisely.
 * 
 * @see https://www.figma.com/design/LYeg7o8lYMFZD4oPJnGTwn/Design-System-2026?node-id=7-3
 */
const textStylePresets = {
  /** Display styles - For large, prominent text */
  display: {
    /** display-lg: 80px, Regular (400), 1.1 line height */
    lg: {
      fontSize: baseTypography.fontSize.displayLg,
      fontWeight: baseTypography.fontWeight.regular,
      lineHeight: baseTypography.lineHeight.tight,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** display-md: 64px, Regular (400), 1.1 line height */
    md: {
      fontSize: baseTypography.fontSize.displayMd,
      fontWeight: baseTypography.fontWeight.regular,
      lineHeight: baseTypography.lineHeight.tight,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
  },

  /** Heading styles - For hierarchical content (h1-h6) */
  heading: {
    /** h1: 48px, Medium (500), 1.2 line height */
    h1: {
      fontSize: baseTypography.fontSize.xxxxxl,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.base,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** h2: 40px, Medium (500), 1.1 line height */
    h2: {
      fontSize: baseTypography.fontSize.xxxxl,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.tight,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** h3: 32px, SemiBold (600), 1.2 line height */
    h3: {
      fontSize: baseTypography.fontSize.xxxl,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.base,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** h4: 24px, SemiBold (600), 1.2 line height */
    h4: {
      fontSize: baseTypography.fontSize.xxl,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.base,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** h5: 20px, SemiBold (600), 1.3 line height */
    h5: {
      fontSize: baseTypography.fontSize.xl,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.comfortable,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** h6: 18px, SemiBold (600), 1.3 line height */
    h6: {
      fontSize: baseTypography.fontSize.lgPlus,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.comfortable,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
  },

  /** Body styles - For main content, paragraphs */
  body: {
    /** body-md: 16px, Medium (500), 1.4 line height */
    md: {
      fontSize: baseTypography.fontSize.lg,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.relaxed,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** body-md-double: 16px, Medium (500), 1.8 line height */
    mdDouble: {
      fontSize: baseTypography.fontSize.lg,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.extraLoose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** body-md-bold: 16px, SemiBold (600), 1.4 line height */
    mdBold: {
      fontSize: baseTypography.fontSize.lg,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.relaxed,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** body-sm: 14px, Medium (500), 1.5 line height */
    sm: {
      fontSize: baseTypography.fontSize.md,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** body-sm-double: 14px, Medium (500), 1.8 line height */
    smDouble: {
      fontSize: baseTypography.fontSize.md,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.extraLoose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** body-sm-bold: 14px, SemiBold (600), 1.5 line height */
    smBold: {
      fontSize: baseTypography.fontSize.md,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
  },

  /** Caption styles - For labels, metadata, helper text */
  caption: {
    /** caption-md: 12px, Medium (500), 1.5 line height */
    md: {
      fontSize: baseTypography.fontSize.sm,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** caption-md-bold: 12px, SemiBold (600), 1.5 line height */
    mdBold: {
      fontSize: baseTypography.fontSize.sm,
      fontWeight: baseTypography.fontWeight.semibold,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** caption-sm: 10px, Medium (500), 1.5 line height */
    sm: {
      fontSize: baseTypography.fontSize.xs,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** caption-sm-bold: 10px, Bold (700), 1.5 line height */
    smBold: {
      fontSize: baseTypography.fontSize.xs,
      fontWeight: baseTypography.fontWeight.bold,
      lineHeight: baseTypography.lineHeight.loose,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
  },

  /** Button styles - For button labels, CTAs */
  button: {
    /** button-lg: 20px, Medium (500), 1.3 line height */
    lg: {
      fontSize: baseTypography.fontSize.xl,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.comfortable,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** button-md: 16px, Medium (500), 1.3 line height */
    md: {
      fontSize: baseTypography.fontSize.lg,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.comfortable,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
    /** button-sm: 14px, Medium (500), 1.4 line height */
    sm: {
      fontSize: baseTypography.fontSize.md,
      fontWeight: baseTypography.fontWeight.medium,
      lineHeight: baseTypography.lineHeight.relaxed,
      letterSpacing: baseTypography.letterSpacing.normal,
    } as TextStyle,
  },
} as const;

/**
 * Complete Typography System
 * Combines base typography scales with semantic presets
 */
export const typography = {
  ...baseTypography,
  presets: textStylePresets,
} as const;

export type Typography = typeof typography;
